import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { IOffersFragment } from 'generated/graphql-gateway';
import { useLoyaltyEngineRewards } from 'hooks/use-loyalty-engine-rewards';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  IncentiveEvaluationMap,
  LoyaltyOfferWithInlineAlertMessage,
} from 'state/loyalty/hooks/types';
import {
  compareBy_idAndErrors,
  createListComparator,
  useConditionalState,
} from 'state/loyalty/hooks/use-conditional-state';
import {
  getWalmartOfferUnavailableMessage,
  isWalmartOffer,
  isWalmartWeekOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { LoyaltyOffer, LoyaltyOfferGroup } from 'state/loyalty/types';
import { useOrderContext } from 'state/order';

import { useLoyaltyIncentivesAvailabilityUtils } from '../loyalty-incentives-components/use-loyalty-incentives-availability';

import { useLoyaltyOffersFilters } from './use-loyalty-offers-filters';
import { reduceOffers } from './utils';

interface UseReduceOffersProps {
  cmsOffers: LoyaltyOffer[];
  cmsOfferGroups: LoyaltyOfferGroup[];
  personalizedOffers: LoyaltyOffer[];
  baseOffers: IOffersFragment[];
  offersLoading: boolean;
}

const compareBy_idAndOffers = createListComparator(['_id', 'offers']);

export function useReduceOffers({
  cmsOffers,
  cmsOfferGroups,
  personalizedOffers,
  baseOffers,
  offersLoading,
}: UseReduceOffersProps) {
  const formatters = useIntl();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const {
    checkLoyaltyOfferAvailability,
    getIncentiveUnavailableMessage,
  } = useLoyaltyIncentivesAvailabilityUtils();
  const { engineRewardsMap } = useLoyaltyEngineRewards();

  const enableSortOffersForServiceMode = useFlag(
    LaunchDarklyFlag.ENABLE_SORT_OFFERS_FOR_SERVICE_MODE
  );
  const { appliedFilters } = useLoyaltyOffersFilters();
  const dispatch = useAppDispatch();

  const [offers, setOffers] = useConditionalState(
    [] as LoyaltyOfferWithInlineAlertMessage[],
    compareBy_idAndErrors
  );
  const [offersInCart, setOffersInCart] = useConditionalState(
    [] as LoyaltyOffer[],
    compareBy_idAndErrors
  );
  const [offerGroups, setOfferGroups] = useConditionalState(
    [] as LoyaltyOfferGroup[],
    compareBy_idAndOffers
  );
  const [reductionLoading, setReductionLoading] = useState(false);

  const { serviceMode } = useOrderContext();

  const runOffersReduction = useCallback(() => {
    if (!cmsOffers?.length && offersLoading) {
      return;
    }

    setReductionLoading(true);

    const [_offers, _offersInCart, _offerGroups] = reduceOffers({
      cmsOffers,
      cmsOfferGroups,
      personalizedOffers,
      baseOffers,
      appliedOffers,
      inRestaurantRedemptionCart,
      checkLoyaltyOfferAvailability,
      getIncentiveUnavailableMessage: (incentive: LoyaltyOffer) => {
        const loyaltyEngineId = incentive.loyaltyEngineId;
        if (!loyaltyEngineId) {
          return;
        }

        // Override Walmart offer max redemption availability error with custom message
        if (isWalmartOffer(incentive) || isWalmartWeekOffer(incentive)) {
          if (incentive.errors && incentive.errors.length) {
            if (
              incentive.errors.find(
                (error: { code: string }) => error.code === 'exceeds-max-redemption'
              )
            ) {
              return getWalmartOfferUnavailableMessage(incentive._id, formatters);
            }
          }
        }

        return getIncentiveUnavailableMessage({
          incentive,
          pointCost: engineRewardsMap[loyaltyEngineId]?.pointCost,
          locked: engineRewardsMap[loyaltyEngineId]?.locked,
        });
      },
      enableSortOffersForServiceMode,
      // @ts-expect-error TS(2322) FIXME: Type '(offerFeedbackMap: IncentiveEvaluationMap) =... Remove this comment to see the full error message
      setOffersFeedbackMap: (offerFeedbackMap: IncentiveEvaluationMap) =>
        dispatch(actions.loyalty.setOffersFeedbackMap(offerFeedbackMap)),
      appliedFilters,
      selectedServiceMode: serviceMode,
      formatters,
    });

    setOffers(_offers);
    setOffersInCart(_offersInCart);
    setOfferGroups(_offerGroups);
    setReductionLoading(false);
  }, [
    offersLoading,
    cmsOffers,
    cmsOfferGroups,
    baseOffers,
    personalizedOffers,
    appliedOffers,
    inRestaurantRedemptionCart,
    checkLoyaltyOfferAvailability,
    getIncentiveUnavailableMessage,
    engineRewardsMap,
    enableSortOffersForServiceMode,
    dispatch,
    appliedFilters,
    formatters,
    serviceMode,
    setOffers, // this is stable but ESLint doesn't know that
    setOffersInCart, // this is stable but ESLint doesn't know that
    setOfferGroups, // this is stable but ESLint doesn't know that
  ]);

  useEffect(() => {
    runOffersReduction();
  }, [runOffersReduction]);

  return {
    offers,
    offersInCart,
    offerGroups,
    reductionLoading,
    reprocessOffers: runOffersReduction,
  };
}
